/**
 * ShipmentKanbanBoard - Kanban de Remessas (somente leitura, colunas por situação).
 *
 * Sem arrastar e soltar: o card muda de coluna quando a situação muda (Realtime).
 * Usa a superfície compartilhada ReadonlyKanbanBoard/Column, como Propostas.
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { ReadonlyKanbanBoard } from '@/components/kanban/ReadonlyKanbanBoard';
import { ReadonlyKanbanColumn } from '@/components/kanban/ReadonlyKanbanColumn';
import { useShipmentKanbanColumn, useShipmentKanbanCounts, type ShipmentKanbanFilters } from '@/hooks/useShipments';
import { useShipmentsKanbanRealtime } from '@/hooks/useShipmentsKanbanRealtime';
import { SHIPMENT_KANBAN_COLUMNS, type ShipmentKanbanColumnDef } from './shipmentKanbanColumns';
import { ShipmentKanbanCard } from './ShipmentKanbanCard';

const brl = (v: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', notation: v >= 100000 ? 'compact' : 'standard', maximumFractionDigits: 2,
}).format(v || 0);

function Column({ column, filters, count, freight, onOpen, now }: {
  column: ShipmentKanbanColumnDef; filters: ShipmentKanbanFilters; count?: number; freight?: number;
  onOpen: (id: string) => void; now: Date;
}) {
  const { shipments, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useShipmentKanbanColumn(column.status, filters);
  return (
    <ReadonlyKanbanColumn label={column.label} count={count ?? shipments.length} barClass={column.barClass}
      headerClass={column.headerClass} itemCount={shipments.length} isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage} hasNextPage={hasNextPage} fetchNextPage={fetchNextPage}
      emptyLabel="Nenhuma remessa"
      headerDetail={freight ? (
        <div className="mt-0.5"><span className="text-[11px] tabular-nums text-muted-foreground">Frete {brl(freight)}</span></div>
      ) : undefined}>
      {shipments.map((s) => <ShipmentKanbanCard key={s.id} shipment={s} onOpen={onOpen} now={now} />)}
    </ReadonlyKanbanColumn>
  );
}

export function ShipmentKanbanBoard({ filters, onOpen }: { filters: ShipmentKanbanFilters; onOpen: (id: string) => void }) {
  const { data } = useShipmentKanbanCounts(filters);
  useShipmentsKanbanRealtime(true);
  // Relógio único do board (faixa de prazo), atualizado a cada minuto
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  return (
    <ReadonlyKanbanBoard>
      {SHIPMENT_KANBAN_COLUMNS.map((c) => (
        <Column key={c.status} column={c} filters={filters} count={data?.counts[c.status] ?? 0}
          freight={Number(data?.freight[c.status] ?? 0)} onOpen={onOpen} now={now} />
      ))}
    </ReadonlyKanbanBoard>
  );
}
